import {ThemePort} from '@shared/application/ports/theme/theme.port';
import {DOCUMENT, inject, Inject, Injectable, signal, WritableSignal} from '@angular/core';
import {ThemeEntity} from '@shared/domain/entities/theme/theme.entity';
import {LocalStoragePort} from '@shared/application/ports/storage/local.storage.port';
import {Title} from '@angular/platform-browser';

@Injectable({providedIn: 'root'})
export class ThemeAdapter implements ThemePort {

  public themes: WritableSignal<ThemeEntity[]> = signal<ThemeEntity[]>([]);
  public themeCurrent = signal<ThemeEntity | null>(null);

  public localStorage: LocalStoragePort = inject(LocalStoragePort);

  constructor(
    @Inject(DOCUMENT)
    private readonly document: Document,
    private readonly title: Title,
  ) {
  }

  public init(): void {
    const code: string | null = this.localStorage.getByKey("_theme_code");
    if (!code) {
      this.updateTheme("light");
    } else {
      this.updateTheme(code);
    }
  }

  public setTheme(theme: ThemeEntity): void {
    this.themeCurrent.set(theme);
    this.localStorage.setByKey("_theme_code", theme.code);
    this.applyThemeDOM();
  }

  public setThemes(themes: ThemeEntity[]): void {
    this.themes.set(themes);
    this.init();
  }

  public updateTheme(code: string): void {
    const themeFound = this.themes().find((theme) => theme.code === code);
    if (!themeFound) return;
    this.localStorage.setByKey("_theme_code", code);
    this.setTheme(themeFound);
    this.applyThemeDOM();
  }

  private applyThemeDOM(): void {
    const root = this.document.documentElement;
    const theme = this.themeCurrent();
    if (!theme) return;

    root.classList.remove('light', 'dark');
    root.classList.add(theme.code);

    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-secondary', theme.secondary);
    root.style.setProperty('--theme-surface', theme.surface);
    root.style.setProperty('--theme-surface-variant', theme.surfaceVariant);
    root.style.setProperty('--theme-text', theme.text);
    root.style.setProperty('--theme-text-variant', theme.textVariant);
  }

  public updateIcon(url: string) {
    let link: HTMLLinkElement | null = this.document.querySelector("link[rel*='icon']");
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }
    link.href = url || 'assets/img/icons/favicon.svg';
  }

  public updateTitle(name: string) {
    this.title.setTitle(name);
  }

}
